import React, { useEffect, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory } from 'react-router-dom';
import FrontBanner from '@/assets/images/front-banner.svg';
import './index.scss';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import { ROUTESLIST } from '../../static/urls';
import apiV2 from "../../middlewares/apiV2";
import useProducts from "../../hooks/useProducts";
import useEntitlements from "../../hooks/useEntitlements";
import { priceCartWithEntitlements, readCart, resolveCatalogProduct } from "@/services/cart";
import { apiErrorMessage, formatMoney, readStoredJson } from "@/utils/common";


const stripePublishKey = process.env.REACT_APP_STRIPE_PUBLISH_KEY;
const stripePromise = stripePublishKey && stripePublishKey.trim() ? loadStripe(stripePublishKey) : null;

const SESSION_REUSE_MS = 30 * 60 * 1000;

// Matches the page: dark accordion rows that lighten when selected, #202020 inputs
// with a yellow focus ring. The Payment Element runs in Stripe's iframe, so Jost is
// loaded there through `fonts`.
const appearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#f8e71b',
    colorText: '#ffffff',
    colorTextSecondary: '#bbbbbb',
    colorTextPlaceholder: '#737373',
    colorBackground: '#202020',
    colorDanger: '#ff6b6b',
    fontFamily: 'Jost, "Helvetica Neue", sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
  rules: {
    '.AccordionItem': {
      backgroundColor: '#0e0e0e',
      border: '1px solid #2f2f2f',
      borderRadius: '12px',
      boxShadow: 'none',
    },
    '.AccordionItem--selected': {
      backgroundColor: '#121212',
      border: '1px solid #737373',
    },
    '.Input': {
      backgroundColor: '#202020',
      border: '1px solid #383838',
      boxShadow: 'none',
    },
    '.Input:focus': {
      border: '1px solid #f8e71b',
      boxShadow: '0 0 0 1px #f8e71b',
    },
    '.Label': {
      color: '#d4d4d4',
    },
  }
};

const stripeFonts = [
  { cssSrc: 'https://fonts.googleapis.com/css2?family=Jost:wght@400;500;600&display=swap' },
];

const resolveProductId = (products, item) => {
  const product = resolveCatalogProduct(products, item);
  return product ? product.id : null;
};

// Catalog amounts and the local constants are both whole dollars.
const itemAmountInCents = (products, item) => {
  const product = resolveCatalogProduct(products, item);
  if (product && product.amount != null) return product.amount * 100;
  return (item.price || 0) * 100;
};

// Promo code and pay button for a Checkout Elements session; Stripe owns the card fields.
const CheckoutControls = ({ actions, session }) => {
  const [promoCode, setPromoCode] = useState("");
  const [promoError, setPromoError] = useState(null);
  const [payError, setPayError] = useState(null);
  const [paying, setPaying] = useState(false);
  const appliedCodes = (session.discountAmounts || []).filter(discount => discount.promotionCode);

  const applyPromoCode = async () => {
    setPromoError(null);
    try {
      const result = await actions.applyPromotionCode(promoCode.trim());
      if (result.type === 'error') setPromoError(result.error.message);
      else setPromoCode("");
    } catch (error) {
      setPromoError(error.message);
    }
  };

  const removePromoCode = async () => {
    setPromoError(null);
    try {
      const result = await actions.removePromotionCode();
      if (result.type === 'error') setPromoError(result.error.message);
    } catch (error) {
      setPromoError(error.message);
    }
  };

  const pay = async () => {
    setPayError(null);
    setPaying(true);
    // On success Stripe redirects to the session's return URL.
    try {
      const result = await actions.confirm();
      if (result.type === 'error') setPayError(result.error.message);
    } catch (error) {
      setPayError(error.message);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="checkout-controls">
      {paying && (
        <div className="loading-overlay">
          <div className="loader" />
        </div>
      )}

      <div className="checkout-promo">
        {appliedCodes.length > 0 ? (
          appliedCodes.map(discount => (
            <div className="checkout-promo__row" key={discount.promotionCode}>
              <span className="checkout-promo__applied">{discount.promotionCode} applied, {discount.amount} off</span>
              <button type="button" className="checkout-btn-blue" onClick={removePromoCode}>Remove</button>
            </div>
          ))
        ) : (
          <>
            <label htmlFor="promo-code" className="checkout-label">Promo code</label>
            <div className="checkout-promo__row">
              <input
                id="promo-code"
                type="text"
                className="checkout-input checkout-input--code"
                value={promoCode}
                onChange={e => setPromoCode(e.target.value)}
              />
              <button
                type="button"
                className="checkout-btn-blue"
                disabled={!promoCode.trim()}
                onClick={applyPromoCode}
              >
                Apply
              </button>
            </div>
          </>
        )}
      </div>
      {promoError && <p className="checkout-error">{promoError}</p>}
      {payError && <p className="checkout-error">{payError}</p>}

      <button
        type="button"
        className="checkout-pay"
        disabled={!session.canConfirm || paying}
        onClick={pay}
      >
        {`Pay ${session.total.total.amount}`}
      </button>
    </div>
  );
};

const DemoPayment = () => {
  const [email, setEmail] = useState("");
  const history = useHistory();
  const [user, setUser] = useState({})
  const [cartItems, setCartItems] = useState([]);
  const { products, loading } = useProducts();
  // The entitlements endpoint is authenticated and a guest can reach checkout, so
  // only ask for it with the key apiV2 attaches as the bearer token. Read once:
  // a 401 here logs the buyer out mid-checkout.
  const [hasSession] = useState(() => Boolean(localStorage.getItem("access_token")));
  const { entitlements } = useEntitlements(hasSession);
  // Keep the cart in step with the stored one, which another tab or the flow the
  // buyer came from can change while this page is open.
  useEffect(() => {
    const syncCart = () => setCartItems(readCart());

    syncCart();
    window.addEventListener("storage", syncCart);
    window.addEventListener("focus", syncCart);

    return () => {
      window.removeEventListener("storage", syncCart);
      window.removeEventListener("focus", syncCart);
    };
  }, []);

  // A buyer who closed the tab during Stripe's redirect never reached the
  // confirmation page, so the cart they paid for is still here. Check the last
  // session before opening a new one, and clear the cart if it was paid.
  const [checkingLastSession, setCheckingLastSession] = useState(
    () => Boolean(localStorage.getItem("checkout_session_id"))
  );
  useEffect(() => {
    const sessionId = localStorage.getItem("checkout_session_id");
    if (!sessionId) return undefined;

    let cancelled = false;
    apiV2
      .get("/session-status", { params: { session_id: sessionId } })
      .then(({ data }) => {
        if (data.status === "open") return;
        localStorage.removeItem("checkout_session_id");
        if (data.status === "complete") {
          localStorage.removeItem("cart");
          localStorage.removeItem("checkout_from");
          if (!cancelled) setCartItems(readCart());
        }
      })
      .catch(error => console.error("Error checking the last checkout session:", error))
      .finally(() => {
        if (!cancelled) setCheckingLastSession(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // A member is shown the discounted price their entitlements resolve to — the same
  // resolution the server charges with. Anything that payload does not price (a
  // membership, a guest's cart) falls back to the public catalog amount.
  const memberPrices = priceCartWithEntitlements(cartItems, entitlements);
  const cartAmounts = cartItems.map(
    (item, index) =>
      (memberPrices[index]
        ? memberPrices[index].amount * 100
        : itemAmountInCents(products, item)) * (item.quantity || 1)
  );
  // What a discounted line would have cost without the membership, so the saving is visible.
  const cartBaseAmounts = cartItems.map((item, index) =>
    memberPrices[index] && memberPrices[index].discounted
      ? memberPrices[index].base * 100 * (item.quantity || 1)
      : null
  );
  const cartTotal = cartAmounts.reduce((sum, amount) => sum + amount, 0);
  useEffect(() => {
    const userInfo = readStoredJson("userInfo", {}) || {};
    setUser(userInfo);
    if (userInfo.email) setEmail(userInfo.email);
  }, []);


  const [isCouponApplied, setIsCouponApplied] = useState(false);
  const [coupon, setCoupon] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const [checkoutActions, setCheckoutActions] = useState(null);
  const [stripeSession, setStripeSession] = useState(null);

  // Each session reserves the buyer's member-discount credits, so one cart gets one
  // session: the mount is keyed on the cart's contents, not on every re-sync.
  const paidCartItems = cartItems.map(({ songurl, ...rest }) => ({
    ...rest,
    product_id: rest.product_id || resolveProductId(products, rest),
  }));
  const paidCartKey =
    hasSession && !checkingLastSession && !loading && cartItems.length > 0 && !isCouponApplied
      ? JSON.stringify(paidCartItems)
      : null;
  const checkoutSession = useRef({ key: null, clientSecret: null, createdAt: 0 });

  useEffect(() => {
    if (!paidCartKey || !stripePromise) return undefined;
    setCheckoutError(null);

    if (paidCartItems.some(item => !item.product_id)) {
      setCheckoutError('This product is no longer available. Please re-add it to your cart.');
      return undefined;
    }

    const from = history?.location?.state?.from || "checkout";
    const fetchClientSecret = () => {
      // Stripe owns the redirect back, which drops router state.
      localStorage.setItem("checkout_from", from);
      // A session expires 35 minutes after it is created; stop reusing it a little before.
      const expired = Date.now() - checkoutSession.current.createdAt > SESSION_REUSE_MS;
      if (checkoutSession.current.key !== paidCartKey || expired) {
        const name = user.nickname || user.name;
        checkoutSession.current = {
          key: paidCartKey,
          createdAt: Date.now(),
          clientSecret: apiV2
            .post("/create-checkout-session", {
              ...(name ? { name } : {}),
              sendInvoice: true,
              cartItems: paidCartItems,
            })
            .then(response => {
              if (response.data.sessionId) localStorage.setItem("checkout_session_id", response.data.sessionId);
              return response.data.clientSecret;
            }),
        };
      }
      return checkoutSession.current.clientSecret;
    };

    let cancelled = false;
    let paymentElement = null;
    stripePromise
      .then(stripe => {
        if (!stripe) throw new Error('Card payments are unavailable right now.');
        return fetchClientSecret().then(clientSecret => {
          if (cancelled) return null;
          const checkout = stripe.initCheckoutElementsSdk({ clientSecret, elementsOptions: { appearance, fonts: stripeFonts } });
          checkout.on('change', session => {
            if (!cancelled) setStripeSession(session);
          });
          paymentElement = checkout.createPaymentElement();
          paymentElement.mount('#checkout');
          return checkout.loadActions();
        });
      })
      .then(result => {
        if (!result || cancelled) return;
        if (result.type === 'error') throw result.error;
        setCheckoutActions(result.actions);
        setStripeSession(result.actions.getSession());
      })
      .catch(error => {
        if (checkoutSession.current.key === paidCartKey) {
          checkoutSession.current = { key: null, clientSecret: null, createdAt: 0 };
        }
        console.error("Error starting checkout:", error);
        if (!cancelled) {
          setCheckoutError(error && !error.response && error.message ? error.message : apiErrorMessage(error));
        }
      });

    return () => {
      cancelled = true;
      setCheckoutActions(null);
      setStripeSession(null);
      if (paymentElement) paymentElement.destroy();
    };
  }, [paidCartKey]);

  const handleCheckout = async (e) => {
    e.preventDefault();

    if (loading || !products || products.length === 0) {
      alert("Loading products, please try again in a moment.");
      return;
    }

    setIsLoading(true);

    try {
      if (!coupon || coupon.trim() === "") {
        alert("Please enter a coupon code.");
        setIsLoading(false);
        return;
      }

      // Same items the card path sends: the file is uploaded separately below.
      const cartItemsForJson = paidCartItems;

      if (cartItemsForJson.some(item => !item.product_id)) {
        alert('This product is no longer available. Please re-add it to your cart.');
        setIsLoading(false);
        return;
      }

      // Create FormData object to send files
      const formData = new FormData();

      // Add regular data
      formData.append('name', user.nickname);
      formData.append('coupon', coupon || null);
      formData.append('sendInvoice', 'false'); // promo code — no invoice, skip without error
      formData.append('cartItems', JSON.stringify(cartItemsForJson));

      // Add the file if it exists
      console.log('Cart items before appending files:', cartItems);
      cartItems.forEach((item, index) => {
        console.log(`Item ${index} songurl:`, item.songurl);
        if (item.songurl && item.songurl.file instanceof File) {
          console.log(`Appending file for item ${index}:`, item.songurl.file);
          formData.append(`songFile`, item.songurl.file, item.songurl.file.name);
        } else if (item.songurl && window.lastUploadedAudioFile) {
          // Fallback to the global variable if the file is not in the item
          console.log(`Using global file for item ${index}:`, window.lastUploadedAudioFile);
          formData.append(`songFile`, window.lastUploadedAudioFile, window.lastUploadedAudioFile.name);
        } else {
          console.log(`No file found for item ${index}`);
        }
      });

      // Call your backend to create the subscription with FormData (coupon folded into the V2 endpoint)
      const response = await apiV2.post(
        "/create-subscription",
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const subscriptionData = response.data;

      setIsLoading(false);
      //data to save : id, created, current_period_end, current_period_start, default_payment_method, customer, latest_invoice.invoice_pdf, plan.id,
      if (subscriptionData?.subscriptions.length > 0 || subscriptionData?.one_time_purchases.length > 0) {
        setIsLoading(false);
        localStorage.removeItem("cart");
        history.push(ROUTESLIST.CONGRATS, {
          from: history?.location?.state?.from || "checkout",
          purchases: subscriptionData.one_time_purchases || [],
        });
      }
    } catch (error) {
      setIsLoading(false);
      const data = error?.response?.data;
      const message =
        data?.message ||
        (data?.errors && Object.values(data.errors).flat()[0]) ||
        data?.error ||
        "Something went wrong. Please try again.";
      alert(message);
      console.log(" : ERROR : => ", error);
    }
  };

  return (
    <main className="page page-shop page-checkout">
      <div className="product-section">
      <div
        className="product-header"
        style={{justifyContent: "space-between" }}>

          <div
            className="back-to-home-section"
            onClick={() => {
              history.push(ROUTESLIST?.SUBSCRIPTIONS);
            }}
          >
            <ArrowBackIcon />
          </div>


        <div className="back-to-home-section-image">
          <img src="https://ample-samples.s3.us-east-2.amazonaws.com/app/front-banner.svg" alt="front-banner" />
        </div>
        <div>
          &nbsp;
       </div>
      </div>

        <div className="checkout-layout">
          <section className="checkout-main">
            <h2 className="checkout-title">Cart</h2>

            {cartItems.length === 0 ? (
              <p>Your cart is empty.</p>
            ) : (
              <>
                {cartItems.map((item, index) => (
                  <div className="checkout-card checkout-item" key={item.id}>
                    <div className="checkout-item__head">
                      <div>
                        <h3 className="checkout-item__name">{item.plan_name}</h3>
                        {item.plan_description && (
                          <p className="checkout-item__desc">{item.plan_description}</p>
                        )}
                      </div>
                      <div className="checkout-item__price">
                        {cartBaseAmounts[index] != null && (
                          <s className="price-was">${formatMoney(cartBaseAmounts[index], true)}</s>
                        )}
                        <strong>${formatMoney(cartAmounts[index], true)}</strong>
                        <span>{cartAmounts[index] ? "USD" : ""}</span>
                      </div>
                    </div>
                    {cartBaseAmounts[index] != null && (
                      <p className="price-saving">Member price — saves ${formatMoney(cartBaseAmounts[index] - cartAmounts[index], true)}</p>
                    )}

                    {item.features && (
                      <ul className="checkout-item__features">
                        {item.features?.map((feature, id) => {
                          return <li key={id}>{feature}</li>;
                        })}
                      </ul>
                    )}
                  </div>
                ))}

                <div className="checkout-card checkout-account">
                  <div className="checkout-field">
                    <label htmlFor="checkout-email" className="checkout-label">Account email</label>
                    <input
                      id="checkout-email"
                      type="email"
                      className="checkout-input"
                      value={email}
                      readOnly
                    />
                  </div>
                  <button
                    type="button"
                    className="checkout-btn-blue"
                    disabled={isLoading}
                    onClick={() => setIsCouponApplied(!isCouponApplied)}
                  >
                    {isLoading ? "Loading..." : isCouponApplied ? "Pay using Stripe" : "Use your Enterprise Code"}
                  </button>
                </div>

                {isCouponApplied ? (
                  <div className="checkout-card checkout-enterprise">
                    <div className="checkout-field">
                      <label htmlFor="enterprise-code" className="checkout-label">Coupon</label>
                      <input
                        id="enterprise-code"
                        type="text"
                        className="checkout-input checkout-input--code"
                        value={coupon}
                        onChange={e => setCoupon(e.target.value)}
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="checkout-pay"
                      disabled={isLoading}
                      onClick={handleCheckout}
                    >
                      {isLoading ? "Loading..." : "Checkout"}
                    </button>
                  </div>
                ) : (
                  <div className="checkout-payment">
                    <h3 className="checkout-subtitle">Select payment method</h3>
                    {!stripePromise ? (
                      <p>Card payments are unavailable right now.</p>
                    ) : !hasSession ? (
                      <p>Log in to pay by card.</p>
                    ) : checkoutError ? (
                      <p className="checkout-error">{checkoutError}</p>
                    ) : (
                      <>
                        {!checkoutActions && (
                          <div className="checkout-skeleton" role="status" aria-live="polite">
                            {[0, 1, 2, 3].map(row => (
                              <div className="checkout-skeleton__row" key={row}>
                                <span className="checkout-skeleton__icon" />
                                <span className="checkout-skeleton__text" />
                              </div>
                            ))}
                            <p className="checkout-hint">Loading checkout...</p>
                          </div>
                        )}
                        <div className={`checkout-element${checkoutActions ? ' is-ready' : ''}`}>
                          <div id="checkout" />
                        </div>
                        {checkoutActions && stripeSession && (
                          <CheckoutControls actions={checkoutActions} session={stripeSession} />
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </section>

          <aside className="checkout-summary">
            <div className="checkout-summary__head">
              <h3>Bill Summary</h3>
              <span>{cartItems.length} {cartItems.length === 1 ? "item" : "items"}</span>
            </div>
            {cartItems.map((item, index) => (
              <div className="checkout-summary__line" key={item.id || index}>
                <span>1x {item.plan_name}</span>
                <span>
                  {cartBaseAmounts[index] != null && (
                    <s className="price-was">${formatMoney(cartBaseAmounts[index], true)}</s>
                  )}
                  ${formatMoney(cartAmounts[index], true)}
                </span>
              </div>
            ))}
            <div className="checkout-summary__divider" />
            <div className="checkout-summary__total">
              <span>Total payable amount</span>
              <strong>${formatMoney(cartTotal, true)}</strong>
            </div>
          </aside>
        </div>

      </div>
    </main>
  );
};

export default DemoPayment;
